import type { DashboardSaveV2Request, Validations, VehicleSectionV2 } from "@credit-acceptance/step6-bff-client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useState } from "react";
import { useController, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Form } from "../../../../../components/ui/form";
import { LabeledValue } from "../../../../../components/ui/labeledValue";
import { SubsectionHeader } from "../../../../../components/ui/subsectionHeader";
import { formatCurrencyForDisplay } from "../../../../../components/ui/uikit/currencyInput";
import { getErrorMessage } from "../../../../../lib/query/errors";
import { unmappedMessages } from "../../../serverViolations";
import { useApplyServerFieldErrors } from "../../../useApplyServerFieldErrors";
import { useRegisterSaveableSection } from "../../../useRegisterSaveableSection";
import { translateVehicleViolations } from "./ContractVehicle.violations";
import {
  buildContractVehicleDefaultValues,
  contractVehicleSchema,
  exceedsContractDateGrace,
  formatMileageForDisplay,
  hasBlockingCpoDateError,
  isNadaLinkEligible,
  isNewVehicle,
  normalizeMileageInput,
  ODOMETER_DISC_MILEAGE_DISPLAY_MAX_LENGTH,
  resolveVehicleCondition,
  stripMileageSeparators,
  TOTAL_SELLING_PRICE_MAX_LENGTH,
  toVehicleSaveRequest,
} from "./ContractVehicleInformation.schema";
import { CheckboxField, CurrencyField, DateField, TextField } from "./Fields";
import { NadaDetailsModal } from "./NadaDetailsModal";
import { OdometerDisclosureModal } from "./OdometerDisclosureModal";

const BOOK_SOURCE_LABEL = "kelly blue book";

// `== null` rather than `=== undefined`: VehicleOnDealV2's optionals are
// serialized as explicit JSON nulls by the BFF ("null when ..." in its own
// field docs), so both absences have to collapse to the same blank.
//
// Blank (not "NA") for an absent value is caps2 parity: `Step6VehicleModelBean`'s
// bookWholesale/bookRetail getters run the raw value through
// `AppUtil.getFormattedAmountWithDecimals`, which short-circuits to `""` on an
// empty input.
function formatBookValue(value?: number | null): string {
  return value == null ? "" : formatCurrencyForDisplay(value);
}

interface ContractVehicleInformationProperties {
  readonly visible?: boolean;
  /** V2 dashboard vehicle section: `vehicleOnDeal` drives the deal data. */
  readonly vehicle?: VehicleSectionV2;
  /** `financial.vehicle.condition`; Total Selling Price is editable only when this is NEW. */
  readonly condition?: string;
  /** `financial.contractDate`; drives spec §5.3's non-blocking "CPO date is well after the contract date" warning. */
  readonly contractDate?: string;
  /** Suppresses the CPO contract-date warning: spec §5.3 exempts pre-approval, which compares against the deal calculator's date instead. */
  readonly isPreApprovalDeal?: boolean;
  /**
   * Whether Odometer Disc Mileage and CPO Date are typeable (Total Selling Price additionally needs a NEW
   * vehicle). Defaults to `false` so the fields fail safe to read-only until a caller resolves the VLA-lock,
   * role, deal-state and pre-approval gates.
   */
  readonly contractVehicleEditable?: boolean;
  /**
   * Whether CPO Date is typeable. Separate again, because caps2 gates it on
   * `!(showSaveButton and applicationStatus != 'Approval')` — i.e. it also locks once the deal reaches
   * Contract Completed, which the role-based `contractVehicleEditable` doesn't cover. Defaults to `false`.
   */
  readonly cpoDateEditable?: boolean;
}

/**
 * Contract Vehicle subsection, seeded from the V2 dashboard `vehicle.vehicleOnDeal`. Year/Make/Model/VIN/Color
 * and Book Wholesale/Retail are read-only `LabeledValue`s; Odometer Disc Mileage, Total Selling Price (NEW only),
 * and CPO Date are RHF + Zod fields on `Fields.tsx`'s dual-mode inputs. Certified Pre-Owned is never
 * user-editable and only controls CPO Date's visibility; CPO Date is required while visible
 * (see `contractVehicleSchema`'s `superRefine`).
 *
 * Registered with the shared save orchestrator via `useRegisterSaveableSection`; edits ride the PUT body's
 * `vehicle` leg, and the response's `vehicle.validations` surface inline (unmapped ones as a banner).
 */
export function ContractVehicleInformation({
  visible = true,
  vehicle,
  condition,
  contractDate,
  isPreApprovalDeal = false,
  contractVehicleEditable = false,
  cpoDateEditable = false,
}: ContractVehicleInformationProperties) {
  const vehicleOnDeal = vehicle?.vehicleOnDeal;
  // Prefers the deal's own condition over `financial.vehicle.condition` — same value, but the Vehicle
  // tab's spec reads it off the vehicle line, and it's the leg that also distinguishes CPO.
  const resolvedCondition = resolveVehicleCondition(vehicle, condition);
  // @TODO (blocked on step6-bff): partial rule. caps2's `disableMsrp` defaults to *true* and is cleared
  // only in `Step6VehicleBackingBean.setVehicleInfoOnLoad` when the dealer's state carries the MTDREBATE
  // CAC rule AND the vehicle is new (`iscacStateExist(CacRuleEnum.MTDREBATE) && isNewVehicle == 'Y'`);
  // `step6_vehicle.xhtml` then also disables it on `preAppDeal`. `VehicleOnDealV2` exposes no MTDREBATE
  // signal (confirmed absent in step6-bff-client 1.0.936), so only the NEW-vehicle half is applied here
  // — which is more permissive than Step 6. The pre-approval half rides on `contractVehicleEditable`.
  const totalSellingPriceEditable = contractVehicleEditable && isNewVehicle(resolvedCondition);
  const { t } = useTranslation();
  const form = useForm({
    resolver: zodResolver(contractVehicleSchema),
    mode: "onBlur",
    defaultValues: buildContractVehicleDefaultValues(vehicle),
  });
  const { control } = form;
  const odometerDiscMileage = useController({ control, name: "odometerDiscMileage" });
  const totalSellingPrice = useController({ control, name: "totalSellingPrice" });
  const isCertifiedPreOwned = useController({ control, name: "isCertifiedPreOwned" });
  const cpoDate = useController({ control, name: "cpoDate" });
  const [serverCpoDateWarning, setServerCpoDateWarning] = useState<string>();

  // Spec §5.4: the Odometer Disc Mileage label is a link to the view-only reason popup. It stays
  // clickable even when the field itself is read-only (spec §3.4's Decision), so it isn't gated on
  // `contractVehicleEditable`.
  const [isOdometerDisclosureOpen, setIsOdometerDisclosureOpen] = useState(false);
  const handleOdometerDiscMileageLabelClick = useCallback(() => {
    setIsOdometerDisclosureOpen(true);
  }, []);

  // Spec §5.1: the VIN label is a link to the NADA popup only when the deal is NADA-eligible and the
  // vehicle isn't new. Same §3.4 Decision as the Odometer link — not gated on `contractVehicleEditable`.
  const nadaLinkEligible = isNadaLinkEligible(vehicle, condition);
  const [isNadaDetailsOpen, setIsNadaDetailsOpen] = useState(false);
  const handleVinLabelClick = useCallback(() => {
    setIsNadaDetailsOpen(true);
  }, []);

  // Spec §5.3's last CPO rule warns without blocking, so it can't live in the Zod schema (an issue
  // there would fail `validate()` and stop the save). Suppressed in pre-approval, where the spec
  // compares against the deal calculator's contract date and the +5-day warning doesn't apply.
  // The blocking check is derived from the value rather than `fieldState.error` because RHF holds
  // no errors until a validation pass runs — on `mode: "onBlur"` the warning would otherwise show
  // next to an already-invalid seeded date and only vanish once the field was touched.
  const cpoDateWarning =
    serverCpoDateWarning ??
    (isCertifiedPreOwned.field.value &&
    !isPreApprovalDeal &&
    !hasBlockingCpoDateError(cpoDate.field.value) &&
    exceedsContractDateGrace(cpoDate.field.value, contractDate)
      ? t("vehicle.contractVehicle.cpoDateExceedsContractDate")
      : undefined);

  // CPO Date validates on change rather than blur (unlike the other fields
  // here) — its future-date check needs to surface as soon as a value lands,
  // not after an extra blur step. `trigger` re-runs the whole schema's
  // superRefine but only surfaces the result for this field.
  function handleCpoDateChange(value: string) {
    cpoDate.field.onChange(value);
    setServerCpoDateWarning(undefined);
    void form.trigger("cpoDate");
  }

  // Same as Signers' own save-time (as opposed to live-validation) error —
  // only set when the shared save call itself fails.
  const [saveError, setSaveError] = useState<string>();
  const { unmapped, apply: applyServerErrors } = useApplyServerFieldErrors(form);
  const { getValues } = form;
  const applyVehicleValidations = useCallback(
    (validations: Validations | undefined) => {
      const { warnings, ...translated } = translateVehicleViolations(validations, {
        isCertifiedPreOwned: getValues("isCertifiedPreOwned"),
      });
      applyServerErrors(translated);
      setServerCpoDateWarning(warnings.get("cpoDate"));
    },
    [applyServerErrors, getValues]
  );

  // The GET omits `vehicle.validations` (only the post-save snapshot carries them), so an absent payload
  // keeps the last save's findings instead of wiping them on the next refetch.
  const vehicleValidationsKey = JSON.stringify(vehicle?.validations);
  useEffect(() => {
    if (vehicle?.validations) {
      applyVehicleValidations(vehicle.validations);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on stringified content, same as Signers' useServerFieldErrors
  }, [vehicleValidationsKey, isCertifiedPreOwned.field.value]);

  useRegisterSaveableSection(
    "contractVehicle",
    {
      validate: async () => !(contractVehicleEditable || cpoDateEditable) || form.trigger(),
      getPayload: (): Partial<DashboardSaveV2Request> =>
        visible && (contractVehicleEditable || cpoDateEditable)
          ? { vehicle: toVehicleSaveRequest(form.getValues()) }
          : {},
      // An empty mileage is sent as "unchanged", so the save would look applied without clearing anything.
      saveBlocker: () =>
        stripMileageSeparators(form.getValues("odometerDiscMileage"))
          ? undefined
          : t("vehicle.contractVehicle.odometerDiscMileageRequiredToSave"),
      onSaved: (outcome) => {
        setSaveError(undefined);
        // The save response is the source of truth, rejected and absent values included.
        if (outcome.refreshFailed) {
          form.reset(undefined, { keepValues: true, keepDirty: false, keepErrors: true });
        } else {
          form.reset(buildContractVehicleDefaultValues(outcome.vehicle), { keepErrors: true });
        }
        // After the reset, so the CPO fields' visibility reflects the saved vehicle.
        applyVehicleValidations(outcome.vehicle?.validations);
      },
      onSaveFailed: (error) => {
        setSaveError(getErrorMessage(error));
      },
    },
    visible
  );

  if (!visible) {
    return null;
  }

  return (
    <div>
      <SubsectionHeader>{t("vehicle.contractVehicle.heading")}</SubsectionHeader>
      {saveError && (
        <p role="alert" className="text-destructive text-2xs px-3 pt-2">
          {saveError}
        </p>
      )}
      {unmappedMessages(unmapped).map((message, index) => (
        <p key={`${index}-${message}`} role="alert" className="text-destructive text-2xs px-3 pt-2">
          {message}
        </p>
      ))}
      <Form {...form}>
        <form onSubmit={(event) => event.preventDefault()} noValidate>
          <div className="gap-(--spacing-3) px-(--spacing-3) py-(--spacing-3) flex flex-col">
            <div className="gap-(--spacing-4) grid grid-cols-2 sm:grid-cols-3">
              <LabeledValue label={t("vehicle.contractVehicle.year")} value={vehicleOnDeal?.year} />
              <LabeledValue label={t("vehicle.contractVehicle.make")} value={vehicleOnDeal?.make} />
              <LabeledValue label={t("vehicle.contractVehicle.model")} value={vehicleOnDeal?.model} />
              <LabeledValue
                label={t("vehicle.contractVehicle.vin")}
                value={vehicleOnDeal?.vehicleIdentificationNumber}
                onLabelClick={nadaLinkEligible ? handleVinLabelClick : undefined}
              />
              <LabeledValue label={t("vehicle.contractVehicle.color")} value={vehicleOnDeal?.color} />
            </div>

            <div className="gap-(--spacing-4) mt-(--spacing-3) grid grid-cols-2 sm:grid-cols-3">
              {/*
                @TODO (blocked on step6-bff): caps2 renders this sub-label only when the book source is
                KBB (`rendered="#{'KBB' == ...vehBookTypeSource}"` on both book rows, fed from
                `vehicleOnDeal.getTypeSource()`). `VehicleOnDealV2` carries no book-source field
                (confirmed absent in step6-bff-client 1.0.936), so it stays hardcoded until the BFF
                exposes `vehBookTypeSource`.
              */}
              <LabeledValue
                label={t("vehicle.contractVehicle.bookWholesale")}
                value={formatBookValue(vehicleOnDeal?.adjustedWholesaleValue)}
                subLabel={vehicleOnDeal?.adjustedWholesaleValue == null ? undefined : BOOK_SOURCE_LABEL}
              />
              <LabeledValue
                label={t("vehicle.contractVehicle.bookRetail")}
                value={formatBookValue(vehicleOnDeal?.adjustedRetailValue)}
                subLabel={vehicleOnDeal?.adjustedRetailValue == null ? undefined : BOOK_SOURCE_LABEL}
              />
            </div>

            <div className="gap-(--spacing-4) grid grid-cols-2">
              <TextField
                label={t("vehicle.contractVehicle.odometerDiscMileage")}
                value={odometerDiscMileage.field.value}
                onChange={odometerDiscMileage.field.onChange}
                formatValue={formatMileageForDisplay}
                parseValue={normalizeMileageInput}
                onBlur={odometerDiscMileage.field.onBlur}
                error={odometerDiscMileage.fieldState.error}
                disabled={!contractVehicleEditable}
                maxLength={ODOMETER_DISC_MILEAGE_DISPLAY_MAX_LENGTH}
                onLabelClick={handleOdometerDiscMileageLabelClick}
              />

              <CurrencyField
                label={t("vehicle.contractVehicle.totalSellingPrice")}
                value={totalSellingPrice.field.value}
                onChange={totalSellingPrice.field.onChange}
                onBlur={totalSellingPrice.field.onBlur}
                error={totalSellingPrice.fieldState.error}
                disabled={!totalSellingPriceEditable}
                maxLength={TOTAL_SELLING_PRICE_MAX_LENGTH}
              />
              {isCertifiedPreOwned.field.value && (
                <div className="gap-(--spacing-2) flex flex-col">
                  <DateField
                    label={t("vehicle.contractVehicle.cpoDate")}
                    value={cpoDate.field.value}
                    onChange={handleCpoDateChange}
                    onBlur={cpoDate.field.onBlur}
                    error={cpoDate.fieldState.error}
                    disabled={!cpoDateEditable}
                  />
                  {cpoDateWarning && (
                    <p role="status" className="text-warning-foreground text-2xs -mt-1">
                      {cpoDateWarning}
                    </p>
                  )}
                  <CheckboxField
                    label={t("vehicle.contractVehicle.certifiedPreOwned")}
                    checked={isCertifiedPreOwned.field.value}
                    error={isCertifiedPreOwned.fieldState.error}
                    disabled
                  />
                </div>
              )}
            </div>
          </div>
        </form>
      </Form>

      <OdometerDisclosureModal
        open={isOdometerDisclosureOpen}
        onOpenChange={setIsOdometerDisclosureOpen}
        odometerChangeReasonPk={vehicleOnDeal?.odometerChangeReasonPk}
      />

      <NadaDetailsModal open={isNadaDetailsOpen} onOpenChange={setIsNadaDetailsOpen} vehicleOnDeal={vehicleOnDeal} />
    </div>
  );
}
